import type { ReactNode, Ref } from "react";
import { useTranslations } from "@acto/web-common/locales";
import { SearchHeaderRow } from "@acto/taskpane/field-sales-2/components/patterns/Search";
import { CustomerRevenueCard } from "@acto/taskpane/field-sales-2/components/primitives/CustomerRevenueCard";
import type { KpiTrend } from "@acto/taskpane/field-sales-2/data-access/useKpiTrend";
import type { ProductGroups } from "@acto/taskpane/field-sales-2/data-access/useProductGroups";
import { LoadMoreFooter } from "@acto/taskpane/field-sales-2/components/primitives/LoadMoreFooter";
import {
  ListTitle,
  ProductTrendItem,
  ProductTrendList,
} from "@acto/taskpane/field-sales-2/components/primitives/ProductTrendList";
import { FilterHeaderRow } from "./FilterHeaderRow";
import { useProductDrill } from "./ProductDrillContext";
import { ListHeaderAnchor } from "./shellControlRow";

type ProductAnalysisCardProps =
  | {
      kind: "revenue";
      /** Card identity at the top level (before any drill). The drilled group overrides it. */
      rootName: string;
      rootExternalReference?: string;
      trend: KpiTrend;
    }
  | {
      kind: "custom";
      /** Rendered as given at every drill level; unlike the revenue card, it does not follow the drilled group. */
      card: ReactNode;
    };

export type ProductAnalysisViewProps = ProductAnalysisCardProps & {
  groups: ProductGroups;
  searchValue: string;
  onSearchChange: (value: string) => void;
  /** Replaces the default list title (e.g. the Produktgruppen/Kunden breakdown toggle at root). */
  titleSlot?: ReactNode;
  /** Marks the top of the header so the page can reveal the nav dock once it scrolls out of view. */
  headerSentinelRef?: Ref<HTMLDivElement>;
  /** Rendered next to the search, e.g. the Wachstum/Rückgang quick-filter trigger. */
  filterSlot?: ReactNode;
  /** Leaf product rows open too, as a `product` drill level. */
  opensProducts?: boolean;
  /** Shown when the level has no rows and no search is active. */
  emptyLabel?: string;
  searchExpanded?: boolean;
  onSearchExpandedChange?: (expanded: boolean) => void;
  listHeaderRef?: Ref<HTMLDivElement>;
};

/**
 * A KPI card plus the drillable, searchable product-group list. Scope-agnostic: callers feed it
 * their own resolved data and either the root identity of a revenue card or a card of their own. Reads the drill level from `ProductDrillContext`, so it
 * must render under a `ProductDrillProvider`.
 */
export function ProductAnalysisView({
  groups,
  searchValue,
  onSearchChange,
  titleSlot,
  headerSentinelRef,
  filterSlot,
  opensProducts = false,
  emptyLabel,
  searchExpanded,
  onSearchExpandedChange,
  listHeaderRef,
  ...cardProps
}: ProductAnalysisViewProps) {
  const t = useTranslations("fieldSalesTwo");
  const tAnalysis = useTranslations("fieldSalesTwo.analysis");

  const { stack, drillInto } = useProductDrill();
  const currentGroup = stack.at(-1);

  const { items, isPending, isEmpty, hasMore, isLoadingMore, loadMore, isSearchActive } = groups;

  // Nothing to search through: the unfiltered level is empty. A zero-result search keeps the box
  // enabled (isSearchActive) so the term can still be cleared.
  const isSearchDisabled = isEmpty && !isSearchActive;

  const handleItemClick = (item: ProductTrendItem) => {
    if (!item.id) return;

    drillInto({
      kind: item.hasChildren ? "group" : "product",
      id: item.id,
      name: item.name,
      externalReference: item.externalReference,
    });
  };

  // A drill step is homogeneous (all groups or all leaf products), so the first row classifies it.
  const isLeafLevel = items.length > 0 && !items[0].hasChildren;
  const listTitle = currentGroup && isLeafLevel ? tAnalysis("products") : tAnalysis("productGroups");

  const header = (
    <FilterHeaderRow filterSlot={filterSlot}>
      <SearchHeaderRow
        key={currentGroup?.id ?? "root"}
        title={titleSlot ?? <ListTitle>{listTitle}</ListTitle>}
        placeholder={t("search.placeholder")}
        value={searchValue}
        onChange={onSearchChange}
        expanded={searchExpanded}
        onExpandedChange={onSearchExpandedChange}
        disabled={isSearchDisabled}
        bareSearch
      />
    </FilterHeaderRow>
  );

  return (
    <>
      {/* While drilled, the revenue card is titled by the group and its KPIs scope to that group's subtree. */}
      {cardProps.kind === "revenue" ? (
        <CustomerRevenueCard
          externalReference={
            (currentGroup ? currentGroup.externalReference : cardProps.rootExternalReference) ?? undefined
          }
          name={currentGroup?.name ?? cardProps.rootName}
          titleVariant="small"
          trend={cardProps.trend}
        />
      ) : (
        cardProps.card
      )}
      <ProductTrendList
        items={opensProducts ? items.map((item) => ({ ...item, canOpen: true })) : items}
        onItemClick={handleItemClick}
        isLoading={isPending}
        emptyLabel={isSearchActive ? t("search.noResults") : (emptyLabel ?? tAnalysis("productGroupsEmpty"))}
        footer={<LoadMoreFooter hasMore={hasMore} isLoadingMore={isLoadingMore} onLoadMore={loadMore} />}
      >
        {listHeaderRef ? <ListHeaderAnchor ref={listHeaderRef}>{header}</ListHeaderAnchor> : header}
        {/* Below the header: once this scrolls under the nav the header has passed, so the dock shows. */}
        <div ref={headerSentinelRef} aria-hidden="true" />
      </ProductTrendList>
    </>
  );
}
